import React, { useState, useEffect } from 'react';
import api from '../services/api';

function Settings() {
  const [user, setUser] = useState(null);
  const [fontSize, setFontSize] = useState('small');
  const [uiScale, setUiScale] = useState('compact');
  const [theme, setTheme] = useState('cloud');

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      const parsed = raw ? JSON.parse(raw) : null;
      setUser(parsed);
    } catch (e) {
      setUser(null);
    }
    // Load per-user display settings (fallback to defaults)
    const settingsKey = (function () {
      try { const u = localStorage.getItem('user'); const p = u ? JSON.parse(u) : null; return p && p.username ? `settings:${p.username}` : null; } catch (e) { return null; }
    })();
    let savedFont = 'small';
    let savedUi = 'compact';
    let savedTheme = 'light';
    if (settingsKey) {
      const rawSettings = localStorage.getItem(settingsKey);
      if (rawSettings) {
        try {
          const parsedSettings = JSON.parse(rawSettings);
          savedFont = parsedSettings.fontSize || savedFont;
          savedUi = parsedSettings.uiScale || savedUi;
          savedTheme = parsedSettings.theme || savedTheme;
        } catch (e) {
          // ignore parse errors and use defaults
        }
      }
    } else {
      // fallback to legacy keys if no user
      savedFont = localStorage.getItem('fontSize') || savedFont;
      savedUi = localStorage.getItem('uiScale') || savedUi;
      savedTheme = localStorage.getItem('theme') || savedTheme;
    }
    setFontSize(savedFont);
    setUiScale(savedUi);
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-font-size', savedFont);
    document.documentElement.setAttribute('data-ui-scale', savedUi);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const handleSaveDisplaySettings = () => {
    // Save per-user if logged in, otherwise fallback to global keys
    const settingsKey = user && user.username ? `settings:${user.username}` : null;
    const payload = { fontSize, uiScale, theme };
    if (settingsKey) {
      localStorage.setItem(settingsKey, JSON.stringify(payload));
    } else {
      localStorage.setItem('fontSize', fontSize);
      localStorage.setItem('uiScale', uiScale);
      localStorage.setItem('theme', theme);
    }

    // Apply settings immediately
    document.documentElement.setAttribute('data-font-size', fontSize);
    document.documentElement.setAttribute('data-ui-scale', uiScale);
    document.documentElement.setAttribute('data-theme', theme);

    alert('Display settings saved!');
  };

  const handleResetDisplaySettings = () => {
    const defFont = 'small';
    const defUi = 'compact';
    const defTheme = 'light';
    setFontSize(defFont);
    setUiScale(defUi);
    setTheme(defTheme);
    // Remove per-user settings if present, otherwise remove legacy keys
    const settingsKey = user && user.username ? `settings:${user.username}` : null;
    if (settingsKey) {
      localStorage.removeItem(settingsKey);
    } else {
      localStorage.removeItem('fontSize');
      localStorage.removeItem('uiScale');
      localStorage.removeItem('theme');
    }
    document.documentElement.setAttribute('data-font-size', defFont);
    document.documentElement.setAttribute('data-ui-scale', defUi);
    document.documentElement.setAttribute('data-theme', defTheme);
    alert('Display settings reset to defaults.');
  };

  const applyTheme = (newTheme) => {
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    // persist immediately to per-user or fallback
    const settingsKey = user && user.username ? `settings:${user.username}` : null;
    if (settingsKey) {
      const raw = localStorage.getItem(settingsKey);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed.theme = newTheme;
      localStorage.setItem(settingsKey, JSON.stringify(parsed));
    } else {
      localStorage.setItem('theme', newTheme);
    }
  };

  const applyFontSize = (newSize) => {
    setFontSize(newSize);
    document.documentElement.setAttribute('data-font-size', newSize);
    const settingsKey = user && user.username ? `settings:${user.username}` : null;
    if (settingsKey) {
      const raw = localStorage.getItem(settingsKey);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed.fontSize = newSize;
      localStorage.setItem(settingsKey, JSON.stringify(parsed));
    } else {
      localStorage.setItem('fontSize', newSize);
    }
  };

  const applyUiScale = (newScale) => {
    setUiScale(newScale);
    document.documentElement.setAttribute('data-ui-scale', newScale);
    const settingsKey = user && user.username ? `settings:${user.username}` : null;
    if (settingsKey) {
      const raw = localStorage.getItem(settingsKey);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed.uiScale = newScale;
      localStorage.setItem(settingsKey, JSON.stringify(parsed));
    } else {
      localStorage.setItem('uiScale', newScale);
    }
  };

  return (
    <div className="vercel-deploy-container">
      <div className="form-section-group" style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Display Settings */}
        <div className="vercel-premium-form">
          <div className="form-section-header">
            <div className="form-section-title-group">
              <h2 className="form-section-title">Display & Interface</h2>
              <p className="form-section-subtitle">Customize the appearance and behavior of the dashboard.</p>
            </div>
          </div>

          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gap: '2rem' }}>

              {/* Theme Selection */}
              <div className="form-group">
                <label style={{ marginBottom: '1rem' }}>Color Theme</label>
                <div className="settings-options-grid">
                  {['cloud', 'dark', 'light'].map(t => (
                    <button
                      key={t}
                      onClick={() => applyTheme(t)}
                      className={`action-btn-premium ${theme === t ? 'primary' : ''}`}
                      style={{ height: '44px', justifyContent: 'center' }}
                    >
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Size Selection */}
              <div className="form-group">
                <label style={{ marginBottom: '1rem' }}>Font Scale</label>
                <div className="settings-options-grid">
                  {['small', 'medium', 'large'].map(size => (
                    <button
                      key={size}
                      onClick={() => applyFontSize(size)}
                      className={`action-btn-premium ${fontSize === size ? 'primary' : ''}`}
                      style={{ height: '44px', justifyContent: 'center', fontSize: size === 'small' ? '0.8rem' : size === 'large' ? '1rem' : '0.9rem' }}
                    >
                      {size.charAt(0).toUpperCase() + size.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* UI Scale Selection */}
              <div className="form-group">
                <label style={{ marginBottom: '1rem' }}>Interface Density</label>
                <div className="settings-options-grid">
                  {['compact', 'comfortable', 'spacious'].map(scale => (
                    <button
                      key={scale}
                      onClick={() => applyUiScale(scale)}
                      className={`action-btn-premium ${uiScale === scale ? 'primary' : ''}`}
                      style={{ height: '44px', justifyContent: 'center' }}
                    >
                      {scale.charAt(0).toUpperCase() + scale.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div style={{ padding: '1rem', background: 'var(--surface-2)', borderRadius: '12px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 700 }}>
                  Active Config: <span style={{ color: 'var(--text-main)' }}>{theme}</span> theme / <span style={{ color: 'var(--text-main)' }}>{fontSize}</span> text / <span style={{ color: 'var(--text-main)' }}>{uiScale}</span> density
                </div>
              </div>

              {/* Action Buttons */}
              <div className="form-section-footer settings-actions-footer">
                <button className="action-btn-premium" onClick={handleResetDisplaySettings}>
                  Reset to Default
                </button>
                <button className="action-btn-premium primary" onClick={handleSaveDisplaySettings} style={{ flex: 1, justifyContent: 'center' }}>
                  Save Interface Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
